import type { DreamBoardCard } from '../services/dreamService'
import { nextWhenWords as goalNextWhenWords, priorityWord as goalPriorityWord } from './goalWords'

/**
 * Words for a dream's progress, priority and next thing (Dreams UX pass
 * 2026-09-06). One place, so the list row, the summary line and the rail say
 * the same thing. A dream's next thing always belongs to one of its goals, so
 * the words name the goal too.
 */

/** "45% along" · null when nothing yet. */
export function progressWords(progress: number | null | undefined): string | null {
  const p = Math.round(Number(progress || 0))
  return p > 0 ? `${p}% along` : null
}

export const priorityWord = goalPriorityWord

/** "Tue, Sep 8 at 6:30 PM PDT" · "Tue, Sep 8" · null. */
export function nextWhenWords(next: DreamBoardCard['next'] | null | undefined, tz?: string | null): string | null {
  if (!next?.date) return null
  return goalNextWhenWords({ kind: 'task', id: 0, title: null, date: next.date, time: next.time ?? null, at: next.at ?? null }, tz)
}

/**
 * The next thing in words: the goal's own next thing plus the goal it is for
 * ("Clear the pantry · for Lose 20 pounds"), or the goal itself when it still
 * needs a next step ("Lose 20 pounds · needs a next step").
 */
export function nextWords(next: DreamBoardCard['next'] | null | undefined): { title: string; sub: string | null } | null {
  if (!next) return null
  if (next.kind === 'goal') return { title: next.title || 'a goal', sub: 'needs a next step' }
  return { title: next.title || '', sub: next.via?.title ? `for ${next.via.title}` : null }
}

/** The kind of thing that got done today, in words. */
export function todayWords(today: DreamBoardCard['today'] | null | undefined): string | null {
  if (!today) return null
  const what = today.kind === 'habit' ? 'habit done' : today.kind === 'skill' ? 'skill practised' : today.kind === 'task' ? 'task done' : 'something done'
  return today.goal_title ? `${what} · ${today.goal_title}` : what
}

/** What a delete removed or set loose, in one sentence — from the backend's cleanup counts. */
export function cleanupWords(cleanup: Record<string, number> | null | undefined): string {
  const c = cleanup || {}
  const bits: string[] = []
  if (c.journey_goals) bits.push(`${c.journey_goals} goal${c.journey_goals === 1 ? '' : 's'} the journey had made but not started`)
  if (c.journey_plans) bits.push('its journey')
  const loose = (c.goals_unparented || 0) + (c.aspirations_unparented || 0)
  const removed = bits.length ? `Removed with it: ${bits.join(' and ')}.` : ''
  const stays = loose ? `${loose} goal${loose === 1 ? ' stays' : 's stay'} on ${loose === 1 ? 'its' : 'their'} own.` : ''
  return [removed, stays].filter(Boolean).join(' ') || 'Nothing else was under it.'
}
